/**
 * TOSS Web Application - Button Component
 *
 * Reusable button component with multiple variants, sizes, and states.
 * Supports icons, loading state, and full width mode.
 *
 * Uses design tokens from TOSS design specification:
 * - Primary Blue: #3F4CBF (--color-primary-500)
 * - Accent Blue: #3F4CBF (--color-accent-500)
 * - Error: #D32F2F (--color-error)
 * - Success: #388E3C (--color-success)
 *
 * Variants:
 * - primary: Blue brand color button (default actions)
 * - secondary: Light background with border
 * - accent: Blue interactive color button (call-to-action)
 * - danger: Error red destructive action button
 * - success: Green success action button
 * - error: Alias for danger variant
 * - outline: Transparent with accent blue border
 * - ghost: Transparent with no border, subtle hover
 * - link: Text button styled as link (accent blue)
 *
 * Sizes (высота у всех одна — `--h-control`; различаются боковые отступы и кегль):
 * - sm: компактная, для плотных рядов
 * - md: базовая
 * - lg: крупная, `--h-control-lg`
 * - icon: квадрат со стороной `--h-control`
 */

import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './Spinner';
import { HintTooltip } from './HintTooltip';

// ============================================================================
// Types
// ============================================================================

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'danger'
  | 'success'
  | 'error'
  | 'outline'
  | 'ghost'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Show loading spinner and disable button */
  isLoading?: boolean;
  /** Show loading spinner and disable button (alias for isLoading) */
  loading?: boolean;
  /** Text to show while loading (replaces children) */
  loadingText?: string;
  /** Icon to show before text */
  leftIcon?: ReactNode;
  /** Icon to show after text */
  rightIcon?: ReactNode;
  /** Make button full width */
  fullWidth?: boolean;
  /**
   * Russian sentence explaining why the action is unavailable. When set, the button
   * is disabled and the reason is shown next to it (and as a `title` on the wrapper).
   * `null` / `undefined` = action available.
   */
  blockedReason?: string | null;
  /**
   * Below 768 px a button with a `leftIcon` shows only the icon — a square of the control height —
   * and keeps its label for screen readers (`max-md:sr-only`). For the «+ Создать» of a list page
   * on a phone, where it shares one row with the search box and the table's «Вид и выгрузка»
   * button (23.09.2026). Ignored without `leftIcon`: a button with no icon would go blank.
   */
  compactOnPhone?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const baseStyles = `
  inline-flex items-center justify-center gap-2
  font-medium rounded-md
  transition-all duration-150
  active:scale-[0.97]
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
  disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:active:scale-100
`;

const variantStyles: Record<ButtonVariant, string> = {
  // Primary: Blue brand color (#3F4CBF)
  primary: `
    bg-primary-500 text-white
    hover:bg-primary-600
    active:bg-primary-700
    focus-visible:ring-primary-500
  `,
  // Secondary: Light background with border
  secondary: `
    bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] border border-[var(--color-border)]
    hover:bg-[var(--color-bg-secondary)]
    focus-visible:ring-primary-500
  `,
  // Accent: Blue interactive color (#3F4CBF)
  accent: `
    bg-accent-500 text-white
    hover:bg-accent-600
    active:bg-accent-700
    focus-visible:ring-accent-500
  `,
  // Danger: Error color (#D32F2F)
  danger: `
    bg-[var(--color-error)] text-white
    hover:bg-[var(--color-error-active)]
    focus-visible:ring-[var(--color-error)]
  `,
  // Success: Success color (#388E3C)
  success: `
    bg-[var(--color-success)] text-white
    hover:bg-[var(--color-success-active)]
    focus-visible:ring-[var(--color-success)]
  `,
  // Error: Alias for danger
  error: `
    bg-[var(--color-error)] text-white
    hover:bg-[var(--color-error-active)]
    focus-visible:ring-[var(--color-error)]
  `,
  // Outline: Transparent with accent border (interactive blue)
  outline: `
    bg-transparent text-accent-500 border border-accent-500
    hover:bg-accent-50
    focus-visible:ring-accent-500
  `,
  // Ghost: Transparent with subtle hover
  ghost: `
    bg-transparent text-[var(--color-text-primary)]
    hover:bg-[var(--color-bg-secondary)]
    focus-visible:ring-primary-500
  `,
  // Link: Text button styled as link (interactive blue)
  link: `
    bg-transparent text-accent-500 underline-offset-4
    hover:underline hover:text-accent-600
    focus-visible:ring-accent-500
    p-0 h-auto
  `,
};

/**
 * Высота у всех размеров ОДНА и приходит из `--h-control` — 44 px на телефоне, 40 на компьютере
 * (27.08.2026, решение владельца; полный разбор — в комментарии к токену в `index.css`).
 *
 * До этого высота бралась из `h-9`/`h-10`, а те считаются от `rem` при плавающем корневом кегле:
 * «кнопка md» была 38.5 px на телефоне и 44 на компьютере, поля же писались пикселями и не
 * плавали. Обещание «44 px, WCAG 2.5.5», стоявшее здесь комментарием, на телефоне не выполнялось
 * ни одним размером — теперь выполняется всеми.
 *
 * `sm` и `md` различаются боковыми отступами, а не высотой: они постоянно стоят в одном ряду с
 * полем и друг с другом (поиск + «Добавить» в тулбарах обоих продуктов), и разная высота там —
 * ровно тот дефект, из-за которого всё это и переделано.
 */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-[var(--h-control)] px-3 text-sm min-w-[44px]',
  md: 'h-[var(--h-control)] px-4 text-sm min-w-[44px]',
  lg: 'h-[var(--h-control-lg)] px-6 text-base min-w-[48px]',
  // Квадрат: `min-w-[44px]` рядом с 40-пиксельной высотой растянул бы её в прямоугольник.
  icon: 'h-[var(--h-control)] w-[var(--h-control)] p-0',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Button component for triggering actions
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>
 *   Save
 * </Button>
 *
 * // With loading state
 * <Button isLoading loadingText="Saving...">
 *   Save
 * </Button>
 *
 * // With icons
 * <Button leftIcon={<PlusIcon />}>
 *   Add Item
 * </Button>
 *
 * // Icon-only button
 * <Button variant="ghost" size="icon" aria-label="Settings">
 *   <SettingsIcon />
 * </Button>
 *
 * // Blocked action: disabled + the reason behind ⓘ on the corner (hover / focus / tap)
 * <Button variant="danger" blockedReason="Невозможно удалить: услуга используется в заявках.">
 *   Удалить
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading,
      loading,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      blockedReason,
      compactOnPhone = false,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // Support both isLoading and loading (alias)
    const loadingState = isLoading ?? loading ?? false;
    const isBlocked = !!blockedReason;
    const isDisabled = disabled || loadingState || isBlocked;
    const reasonId = useId();

    // Combine all classes
    const compact = compactOnPhone && !!leftIcon;

    const buttonClasses = [
      baseStyles,
      variantStyles[variant],
      variant !== 'link' ? sizeStyles[size] : '',
      compact ? 'max-md:w-[var(--h-control)] max-md:min-w-0 max-md:px-0 max-md:gap-0' : '',
      fullWidth ? 'w-full' : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    const buttonElement = (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        disabled={isDisabled}
        aria-describedby={isBlocked ? reasonId : undefined}
        {...props}
      >
        {loadingState ? (
          <>
            <Spinner size="sm" className="text-current" />
            {loadingText ?? children}
          </>
        ) : (
          <>
            {leftIcon && <span className="shrink-0">{leftIcon}</span>}
            {compact ? <span className="max-md:sr-only">{children}</span> : children}
            {rightIcon && <span className="shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );

    if (!isBlocked) return buttonElement;

    // The wrapper is required: `disabled:pointer-events-none` on the button kills every hover
    // on it, so the reason has to live on a non-disabled sibling. Until 03.09.2026 that sibling
    // was the sentence itself, printed beside the button; in a dialog footer «Невозможно
    // удалить: станция используется в осмотрах.» pushed Отмена / Сохранить onto a second line,
    // and the owner asked for the pricing-page recipe: «прячь в иконку и показать как
    // всплывающий текст». So: a quiet ⓘ on the button's corner, the text on hover / focus / tap
    // (`HintTooltip`), and the sentence kept in the DOM visually hidden as `aria-describedby`.
    return (
      <span className={`relative inline-flex min-w-0 ${fullWidth ? 'w-full' : ''}`}>
        {buttonElement}
        <span id={reasonId} className="sr-only">
          {blockedReason}
        </span>
        <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)]">
          <HintTooltip text={blockedReason} label="Почему действие недоступно" tone="quiet" />
        </span>
      </span>
    );
  }
);

Button.displayName = 'Button';

// ============================================================================
// Icon Button Variant
// ============================================================================

export interface IconButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'loadingText'> {
  /** Accessibility label for the button */
  'aria-label': string;
}

/**
 * Icon-only button with required aria-label
 *
 * @example
 * ```tsx
 * <IconButton aria-label="Delete" onClick={handleDelete}>
 *   <TrashIcon />
 * </IconButton>
 * ```
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'icon', variant = 'ghost', ...props }, ref) => {
    return <Button ref={ref} size={size} variant={variant} {...props} />;
  }
);

IconButton.displayName = 'IconButton';

// ============================================================================
// Button Group
// ============================================================================

export interface ButtonGroupProps {
  children: ReactNode;
  /** Orientation of the group */
  orientation?: 'horizontal' | 'vertical';
  /** Additional class names */
  className?: string;
}

/**
 * Group buttons together with proper spacing and border radius
 *
 * @example
 * ```tsx
 * <ButtonGroup>
 *   <Button variant="outline">Edit</Button>
 *   <Button variant="outline">Delete</Button>
 * </ButtonGroup>
 * ```
 */
export function ButtonGroup({
  children,
  orientation = 'horizontal',
  className = '',
}: ButtonGroupProps): JSX.Element {
  const groupClasses = [
    'inline-flex',
    orientation === 'horizontal'
      ? 'flex-row [&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:last-child)]:border-r-0'
      : 'flex-col [&>*:not(:first-child)]:rounded-t-none [&>*:not(:last-child)]:rounded-b-none [&>*:not(:last-child)]:border-b-0',
    className,
  ].join(' ');

  return <div className={groupClasses}>{children}</div>;
}

export default Button;
